/**
 * Outbound update to an entity's custom-field values, mirroring the SDK's
 * `roboto.updates.CustomFieldChangeset`: `set_fields` assigns values by field
 * name, `clear_fields` nulls them. An omitted member is a no-op; a field must
 * not appear in both (server and builder reject overlap).
 */
export interface CustomFieldChangeset {
  set_fields?: Record<string, unknown>;
  clear_fields?: string[];
}

/**
 * Builds a `CustomFieldChangeset`, omitting empty parts so an update carries
 * only what changed. Throws if a field appears in both `setFields` and
 * `clearFields`.
 */
export function buildCustomFieldChangeset(input: {
  setFields?: Record<string, unknown>;
  clearFields?: string[];
}): CustomFieldChangeset {
  const setNames = input.setFields ? Object.keys(input.setFields) : [];
  const clearNames = input.clearFields ?? [];

  const overlap = clearNames.filter((name) => setNames.includes(name));
  if (overlap.length > 0) {
    throw new Error(
      `Custom-field changeset cannot set and clear the same field(s): ${overlap.join(", ")}`,
    );
  }

  const changeset: CustomFieldChangeset = {};
  if (setNames.length > 0) {
    changeset.set_fields = input.setFields;
  }
  if (clearNames.length > 0) {
    changeset.clear_fields = clearNames;
  }
  return changeset;
}
